import { FormEvent, PropsWithChildren, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Box } from '@mui/material'
import { useIsFetching, useIsMutating } from 'react-query'

import { useHeapDispatch } from 'context/Heap'
import { useSessionDispatch, useSessionState } from 'context/Session'
import { useFlow, useStartOver } from 'utils/flow'
import { useFlowState, useNext } from 'utils/hook'

import useSubmit from '../useSubmit'

type FormProps = PropsWithChildren

const liveRegionSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
}

export default function Form({ children }: FormProps): JSX.Element {
  const fetchingNumber = useIsFetching()
  const mutatingNumber = useIsMutating()
  const { isSubmitting } = useSessionState()

  const { isSubmitted } = useSessionState()

  const startOver = useStartOver()

  const next = useNext()
  const { mutate: send } = useSubmit()
  const { data: flow } = useFlow()

  const flowState = useFlowState()

  const [sessionDispatch] = useSessionDispatch()
  const [heapDispatch] = useHeapDispatch()
  const didNavigateAfterSubmit = useRef(false)

  const { trigger, handleSubmit } = useFormContext()

  const onValidSubmit = async (): Promise<void> => {
    try {
      const isValid = await trigger()
      if (flow != null && isValid) {
        send(flowState, {
          onSuccess: ({ data_output_results: outputResult }) => {
            // Flag used by page transitions when Submit + Next share one transitions list.
            // See flow-builder skill: heap._documentSubmitted
            heapDispatch({
              type: 'add',
              fields: {
                ...(outputResult.length > 0 ? { data_output_results: outputResult } : {}),
                _documentSubmitted: 'true',
              },
            })

            sessionDispatch({ type: 'submit' })
          },
          onError: () => {
            stopSubmit()
            toast.error('Something went wrong on submit')
          },
        })
      } else {
        stopSubmit()
      }
    } catch (error) {
      console.log('error onSubmit', error)
      toast.error('Something went wrong on form data collect')
      stopSubmit()
    }
  }

  const stopSubmit = (): void => {
    sessionDispatch({ type: 'setIsSubmitting', isSubmitting: false })
  }

  const onFormSubmit = handleSubmit(onValidSubmit, stopSubmit)

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    e.stopPropagation()

    const focusedElement = document.querySelector(':focus')

    // To prevent situation with one input and Enter key
    if (
      (e.nativeEvent as SubmitEvent).submitter !== null &&
      (focusedElement === null || focusedElement?.tagName === 'BUTTON')
    ) {
      sessionDispatch({ type: 'setIsSubmitting', isSubmitting: true })
    }
  }

  useEffect(() => {
    if (isSubmitting && fetchingNumber === 0 && mutatingNumber === 0) {
      void onFormSubmit()
    }
  }, [fetchingNumber, mutatingNumber, isSubmitting])

  useEffect(() => {
    if (isSubmitted) {
      // beforeunload does not work on mobile Safari
      const isSafariOniPhone =
        /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
        /iPhone/i.test(navigator.userAgent)

      if (isSafariOniPhone) {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') {
            startOver()
          }
        })
      } else {
        window.addEventListener('beforeunload', startOver)
      }

      return () => {
        if (isSafariOniPhone) {
          document.removeEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
              startOver()
            }
          })
        } else {
          window.removeEventListener('beforeunload', startOver)
        }
      }
    }
  }, [isSubmitted])

  useEffect(() => {
    if (!isSubmitted) {
      didNavigateAfterSubmit.current = false
      return
    }

    // Navigate once after submit, forcing the submit transition path so we don't lose
    // to the default Next route before heap updates (or with a stale heap flag).
    // next({ documentSubmitted: true }) skips re-validation (see useNext) so a post-submit
    // field reset cannot block navigation; the ref only prevents duplicate effect runs.
    if (didNavigateAfterSubmit.current) {
      return
    }

    didNavigateAfterSubmit.current = true
    next({ documentSubmitted: true })
    // next is recreated each render; ref guards against double navigation
  }, [isSubmitted])

  return (
    <form id="form" onSubmit={onSubmit} aria-busy={isSubmitting}>
      <Box role="status" aria-live="polite" aria-atomic="true" sx={liveRegionSx}>
        {isSubmitting
          ? 'Submitting, please wait'
          : isSubmitted
            ? 'Claim successfully submitted!'
            : ''}
      </Box>
      {children}
    </form>
  )
}
